import axios from 'axios';
import { getApiUrl } from '../runtimeEnv';

// Create axios instance
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('interpreterToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 Adding auth token to request:', config.url);
    } else {
      console.log('⚠️ No auth token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const buildApiError = (message) => {
      const normalizedError = new Error(message);
      // Preserve axios metadata so callers can branch on status/code.
      normalizedError.code = error.code;
      normalizedError.config = error.config;
      normalizedError.request = error.request;
      normalizedError.response = error.response;
      normalizedError.status = error.response?.status;
      normalizedError.isAxiosError = !!error.isAxiosError;
      return normalizedError;
    };

    console.error('API Response Error:', error);
    
    if (error.response?.status === 429) {
      throw buildApiError('Too many requests. Please try again later.');
    }
    
    if (error.response?.status >= 500) {
      throw buildApiError('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      if (error.response.data.errors) {
        // Format validation errors
        const errorMessages = error.response.data.errors.map(err => 
          `${err.path}: ${err.msg}`
        ).join(', ');
        throw buildApiError(`Validation errors: ${errorMessages}`);
      }
      throw buildApiError(error.response.data.message);
    }
    
    throw buildApiError(error.message || 'An unexpected error occurred');
  }
);



// Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await api({
      url: endpoint,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Auth API
export const authAPI = {
  // Interpreter login
  interpreterLogin: (credentials) => {
    return api.post('/auth/interpreter/login', credentials);
  },
  
  // Admin login
  adminLogin: (credentials) => {
    return api.post('/auth/login', credentials);
  },
  
  // Logout
  logout: () => {
    return api.post('/auth/logout');
  },
  
  // Get profile
  getProfile: () => {
    return api.get('/auth/profile');
  },
  
  // Change password
  changePassword: (passwordData) => {
    return api.post('/auth/change-password', passwordData);
  },
  
  // Forgot password
  forgotPassword: (email) => {
    return api.post('/auth/forgot-password', { email });
  },
  
  // Reset password
  resetPassword: (tokenData) => {
    return api.post('/auth/reset-password', tokenData);
  },
};

// Interpreter API
export const interpreterAPI = {
  // Trolley payout onboarding (self-service)
  getTrolleyStatus: () => api.get('/interpreters/me/trolley/status').then((r) => r.data),
  getTrolleyWidgetLink: () => api.post('/interpreters/me/trolley/widget-link').then((r) => r.data),

  // Look up interpreter by email; if found and not registered, send invite email
  lookupByEmail: (email) => api.post('/interpreters/lookup-by-email', { email }).then((r) => r.data),

  // Create interpreter profile (with file uploads)
  createProfile: (profileData) => {
    return api.post('/interpreters', profileData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Get interpreter profile
  getProfile: () => {
    return api.get('/interpreters/profile');
  },
  
  // Update interpreter profile (direct update - deprecated in favor of update approval workflow)
  updateProfile: (profileData) => {
    return api.put('/interpreters/profile', profileData);
  },
  
  // Submit profile update (requires admin approval)
  submitProfileUpdate: (profileData) => {
    return api.post('/interpreters/profile/update', profileData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Get pending profile update
  getPendingUpdate: () => {
    return api.get('/interpreters/profile/pending-update');
  },
  
  // Cancel pending profile update
  cancelPendingUpdate: () => {
    return api.delete('/interpreters/profile/pending-update');
  },

  getServiceAreas: () => api.get('/interpreters/service-areas'),
  createServiceArea: (data) => api.post('/interpreters/service-areas', data),
  updateServiceArea: (areaId, data) => api.put(`/interpreters/service-areas/${areaId}`, data),
  deleteServiceArea: (areaId) => api.delete(`/interpreters/service-areas/${areaId}`),

  getAgencyMembers: () => api.get('/interpreters/agency-members'),
  createTeamMember: (memberData) => api.post('/interpreters/agency-members/create', memberData),
  removeAgencyMember: (memberId) => api.delete(`/interpreters/agency-members/${memberId}`),
};

export const transportationProviderAPI = {
  lookupByEmail: (email) =>
    api.post('/transportation-providers/lookup-by-email', { email }).then((r) => r.data),

  createProfile: (profileData) =>
    api.post('/transportation-providers', profileData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  completeProfile: (token, profileData) =>
    api.post(`/transportation-providers/complete/${token}`, profileData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getDocumentRequest: (token) =>
    api.get(`/transportation-providers/document-requests/${token}`).then((r) => r.data),

  submitDocumentRequest: (token, formData) =>
    api.post(`/transportation-providers/document-requests/${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getMyTrips: (params = {}) =>
    api.get('/transportation-providers/my-trips', { params }).then((r) => r.data),

  getTrip: (jobId) =>
    api.get(`/transportation-providers/my-trips/${jobId}`).then((r) => r.data),

  getAvailableTrips: () =>
    api.get('/transportation-providers/available-trips').then((r) => r.data),

  getAvailableTrip: (jobId) =>
    api.get(`/transportation-providers/available-trips/${jobId}`).then((r) => r.data),

  submitQuote: (jobId, payload) =>
    api.post(`/transportation-providers/available-trips/${jobId}/quote`, payload).then((r) => r.data),

  declineTrip: (jobId, payload) =>
    api.post(`/transportation-providers/available-trips/${jobId}/decline`, payload).then((r) => r.data),

  startTracking: (jobId) =>
    api.post(`/transportation-providers/my-trips/${jobId}/tracking/start`).then((r) => r.data),

  stopTracking: (jobId) =>
    api.post(`/transportation-providers/my-trips/${jobId}/tracking/stop`).then((r) => r.data),

  sendTrackingPing: (jobId, payload) =>
    api.post(`/transportation-providers/my-trips/${jobId}/tracking/ping`, payload).then((r) => r.data),

  getTrackingStatus: (jobId) =>
    api.get(`/transportation-providers/my-trips/${jobId}/tracking/status`).then((r) => r.data),

  confirmTrip: (jobId) =>
    api.post(`/transportation-providers/my-trips/${jobId}/confirm`).then((r) => r.data),

  completeTrip: (jobId) =>
    api.post(`/transportation-providers/my-trips/${jobId}/complete`).then((r) => r.data),

  submitCompletionReport: (jobId, payload) =>
    api.post(`/transportation-providers/my-trips/${jobId}/completion-report`, payload).then((r) => r.data),
};

// Parametric Data API
export const parametricAPI = {
  // Get all parametric data
  getAllParametricData: () => {
    return api.get('/parametric/all');
  },


};



export default api;